import { useState } from "react";

function DotGrid({ cols = 6, rows = 5, highlightPositions = [] }) {
  const dots = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const isHighlighted = highlightPositions.some(([hr, hc]) => hr === r && hc === c);
      dots.push(
        <circle
          key={`${r}-${c}`}
          cx={c * 44 + 20}
          cy={r * 44 + 20}
          r={isHighlighted ? 18 : 14}
          fill={isHighlighted ? "#a78bfa" : "#ddd6fe"}
          opacity={isHighlighted ? 0.85 : 0.35 + c * 0.08}
        />
      );
    }
  }
  return (
    <svg viewBox={`0 0 ${cols * 44} ${rows * 44}`} style={{ width: "100%", maxWidth: cols * 44, height: "auto" }}>
      {dots}
    </svg>
  );
}

function ArrowIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

const LOGOS = [
  {
    id: "morgan",
    el: (
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
        <span style={{ fontFamily: "serif", fontSize: "1rem", fontWeight: 600, letterSpacing: "0.1em", color: "#0f0f1a" }}>MORGAN CREEK</span>
        <span style={{ fontFamily: "serif", fontSize: "0.6rem", fontWeight: 400, letterSpacing: "0.25em", color: "#6b7280", marginTop: 2 }}>DIGITAL ASSETS</span>
      </div>
    ),
  },
  {
    id: "circle-deco",
    el: <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#ddd6fe", opacity: 0.7 }} />,
  },
  {
    id: "fprime",
    el: <span style={{ fontSize: "1.4rem", fontWeight: 400, letterSpacing: "0.12em", color: "#0f0f1a" }}>F·PRIME</span>,
  },
  {
    id: "parafi",
    el: (
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
        <span style={{ fontSize: "1.25rem", fontWeight: 400, letterSpacing: "0.18em", color: "#0f0f1a" }}>PARAFI</span>
        <span style={{ fontSize: "0.6rem", fontWeight: 400, letterSpacing: "0.35em", color: "#6b7280", marginTop: 2 }}>CAPITAL</span>
      </div>
    ),
  },
  {
    id: "pill1",
    el: <div style={{ width: 140, height: 46, borderRadius: 999, border: "2px solid #a78bfa" }} />,
  },
  {
    id: "hatch",
    el: (
      <svg width="32" height="44" viewBox="0 0 36 48" style={{ opacity: 0.5 }}>
        {[0, 8, 16, 24, 32].map((x) => (
          <line key={x} x1={x} y1="0" x2={x + 12} y2="48" stroke="#a78bfa" strokeWidth="2.5" />
        ))}
      </svg>
    ),
  },
  {
    id: "gemini",
    el: (
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <circle cx="11" cy="11" r="10" stroke="#0f0f1a" strokeWidth="1.5" />
          <text x="6" y="15" fontSize="11" fill="#0f0f1a" fontWeight="700">G</text>
        </svg>
        <span style={{ fontSize: "1.15rem", fontWeight: 600, letterSpacing: "0.1em", color: "#0f0f1a" }}>GEMINI</span>
      </div>
    ),
  },
  {
    id: "pill2",
    el: <div style={{ width: 140, height: 46, borderRadius: 999, border: "2px solid #a78bfa" }} />,
  },
  {
    id: "blockchain",
    el: (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M9 2L2 6V12L9 16L16 12V6L9 2Z" fill="#0f0f1a" />
          <path d="M9 2L2 6V12L9 16L16 12V6L9 2Z" fill="none" stroke="#f8f7ff" strokeWidth="1" />
        </svg>
        <span style={{ fontSize: "1rem", fontWeight: 400, color: "#0f0f1a" }}>Blockchain.com</span>
      </div>
    ),
  },
  {
    id: "lightspeed",
    el: (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M2 16L9 2L11 9L18 7L9 18L7 11Z" fill="#0f0f1a" />
        </svg>
        <span style={{ fontSize: "1.15rem", fontWeight: 600, color: "#0f0f1a" }}>Lightspeed</span>
      </div>
    ),
  },
  {
    id: "circle2",
    el: <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#c4b5fd", opacity: 0.6 }} />,
  },
];

function InvestorMarquee() {
  const doubled = [...LOGOS, ...LOGOS];

  return (
    <>
      <style>{`
        @keyframes marquee-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-inner {
          animation: marquee-scroll 30s linear infinite;
        }
        .marquee-outer:hover .marquee-inner {
          animation-play-state: paused;
        }
      `}</style>

      <div
        className="marquee-outer"
        style={{
          overflow: "hidden",
          width: "100%",
          position: "relative",
          padding: "12px 0",
        }}
      >
        {/* Left fade */}
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0, width: 100,
          background: "linear-gradient(to right, #f8f7ff 30%, transparent)",
          zIndex: 2, pointerEvents: "none",
        }} />
        {/* Right fade */}
        <div style={{
          position: "absolute", right: 0, top: 0, bottom: 0, width: 100,
          background: "linear-gradient(to left, #f8f7ff 30%, transparent)",
          zIndex: 2, pointerEvents: "none",
        }} />

        <div
          className="marquee-inner"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 56,
            width: "max-content",
          }}
        >
          {doubled.map((logo, i) => (
            <div key={`${logo.id}-${i}`} style={{ flexShrink: 0 }}>
              {logo.el}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function MediaCard({ item }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#ffffff",
        border: `1px solid ${hovered ? "#c4b5fd" : "#e5e7eb"}`,
        borderRadius: 16,
        padding: "clamp(20px, 3vw, 28px) clamp(16px, 2.5vw, 24px) clamp(16px, 2.5vw, 24px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "clamp(160px, 20vw, 200px)",
        cursor: "pointer",
        transition: "box-shadow 0.3s, border-color 0.3s, transform 0.3s",
        boxShadow: hovered ? "0 12px 40px rgba(124,58,237,0.12)" : "none",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
      }}
    >
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          {item.outletIcon && (
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 24, height: 24, borderRadius: "50%",
              background: item.outletColor, color: "#fff",
              fontSize: "0.75rem", fontWeight: 600, flexShrink: 0,
            }}>
              {item.outletIcon}
            </span>
          )}
          <span style={{
            fontSize: item.outletBold ? "clamp(1rem, 2vw, 1.3rem)" : "0.95rem",
            fontWeight: item.outletBold ? 800 : 500,
            color: item.outletBold ? "#0f0f1a" : item.outletColor || "#0f0f1a",
            letterSpacing: item.outletBold ? "-0.01em" : "normal",
          }}>
            {item.outlet}
          </span>
        </div>
        <p style={{
          fontSize: "clamp(0.82rem, 1.2vw, 0.92rem)",
          color: "#374151", lineHeight: 1.65, margin: "0 0 20px",
        }}>
          {item.headline}
        </p>
      </div>
      <div style={{
        width: 36, height: 36, borderRadius: "50%",
        border: `1.5px solid ${hovered ? "#7c3aed" : "#d1d5db"}`,
        background: hovered ? "#7c3aed" : "transparent",
        color: hovered ? "#ffffff" : "#374151",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        transition: "background 0.3s, border-color 0.3s, color 0.3s, transform 0.3s",
        transform: hovered ? "rotate(-40deg)" : "rotate(0deg)",
        flexShrink: 0,
      }}>
        <ArrowIcon size={14} />
      </div>
    </div>
  );
}

const mediaItems = [
  { outlet: "The Information", outletColor: "#e53e3e", outletIcon: "I", headline: "Checking in on Crypto's Most Promising Startups" },
  { outlet: "Forbes", outletBold: true, outletColor: "#000", headline: "U.S. Government Owns Way More Bitcoin Than Any Other Country–So Why Aren't They Selling It?" },
  { outlet: "Nasdaq", outletColor: "#0088cc", outletIcon: "N", headline: "Why Large-Scale Institutional Adoption of Blockchain Technology Is Coming" },
  { outlet: "Bloomberg", outletBold: true, outletColor: "#000", headline: "Celsius Creditors Flag 'Suspicious' FTX Connections" },
];

export default function Tokens() {
  return (
    <div style={{
      background: "#f8f7ff",
      color: "#0f0f1a",
      
    }}>

      {/* ── INVESTORS SECTION ── */}
      <section style={{
        paddingTop: "clamp(48px, 8vw, 80px)",
        paddingBottom: "clamp(40px, 6vw, 72px)",
        maxWidth: 1400,
        margin: "0 auto",
        boxSizing: "border-box",
      }}>
        {/* Tag + Headline */}
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "clamp(16px, 3vw, 48px)",
          alignItems: "flex-start",
          marginBottom: "clamp(32px, 5vw, 56px)",
          padding: "0 clamp(16px, 5vw, 64px)",
        }}>
          <div style={{ flexShrink: 0, paddingTop: 6 }}>
            <span style={{
              display: "inline-block",
              background: "#ede9fe", color: "#4b5563",
              fontSize: "0.72rem", fontWeight: 600,
              letterSpacing: "0.1em", textTransform: "uppercase",
              padding: "5px 12px", borderRadius: 6, whiteSpace: "nowrap",
            }}>
              Our Investors
            </span>
          </div>
          <h2 style={{
            fontSize: "clamp(1.8rem, 4vw, 3.4rem)",
            fontWeight: 600, color: "#0f0f1a",
            margin: 0, lineHeight: 1.15, letterSpacing: "-0.02em",
          }}>
            Backed by the leading minds
            <br />in blockchain and technology
          </h2>
        </div>

        {/* Marquee — full bleed edge to edge */}
        <InvestorMarquee />
      </section>

      {/* Divider */}
      <div style={{ height: 1, background: "#e5e7eb", margin: "0 clamp(16px, 5vw, 64px)" }} />

      {/* ── MEDIA SECTION ── */}
      <section style={{
        padding: "clamp(48px, 8vw, 80px) clamp(16px, 5vw, 64px)",
        maxWidth: 1400, margin: "0 auto", boxSizing: "border-box",
      }}>
        <div style={{ marginBottom: "clamp(20px, 3vw, 32px)" }}>
          <span style={{
            display: "inline-block", background: "#ede9fe", color: "#4b5563",
            fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.1em",
            textTransform: "uppercase", padding: "5px 12px", borderRadius: 6,
          }}>
            Media
          </span>
        </div>

        <div style={{
          display: "flex", flexWrap: "wrap",
          gap: "clamp(24px, 4vw, 40px)",
          alignItems: "flex-start", justifyContent: "space-between",
          marginBottom: "clamp(32px, 5vw, 56px)",
        }}>
          <h2 style={{
            fontSize: "clamp(1.6rem, 3.5vw, 3rem)",
            fontWeight: 600, color: "#0f0f1a", margin: 0,
            lineHeight: 1.2, letterSpacing: "-0.02em",
            maxWidth: 600, flex: "1 1 260px",
          }}>
            Here are some of our most recent media mentions, coverage, and press releases
          </h2>
          <div style={{ flex: "0 0 auto", maxWidth: "clamp(120px, 20vw, 264px)", width: "100%" }}>
            <DotGrid cols={6} rows={5} highlightPositions={[[1, 2], [3, 1]]} />
          </div>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
          gap: "clamp(12px, 2vw, 20px)",
        }}>
          {mediaItems.map((item, i) => (
            <MediaCard key={i} item={item} />
          ))}
        </div>
      </section>
    </div>
  );
}